import { t } from "../i18n";
import React, { useState, useEffect, useRef } from "react";
import api from "../services/api";

function CommentDrawer({ video, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText]         = useState("");
  const [loading, setLoading]   = useState(true);
  const [posting, setPosting]   = useState(false);
  const [error, setError]       = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    fetchComments();
    // Focus input after drawer opens
    setTimeout(() => inputRef.current?.focus(), 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/videos/${video.id}/comments/`);
      const data = Array.isArray(res.data) ? res.data : [];
      setComments(data);
    } catch(e) {
      console.error("Fetch comments error:", e);
      setError("Could not load comments.");
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const trimmed = text.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    setError("");
    try {
      const res = await api.post(`/videos/${video.id}/comments/`, { text: trimmed });
      const newComments = [res.data, ...comments];
      setComments(newComments);
      setText("");
    } catch(e) {
      console.error("Post comment error:", e);
      if (e.response?.status === 401) {
        setError("Please log in to comment.");
      } else {
        setError("Failed to post comment. Try again.");
      }
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      style={{ position:"absolute", bottom:0, left:0, right:0, zIndex:9999, height:"48vh" }}
      onClick={onClose}
    >
      {/* Backdrop */}
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.35)", zIndex:0 }} />

      {/* Drawer */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position:"absolute", bottom:0, left:0, right:0,
          background:"#1a1a1a",
          borderRadius:"20px 20px 0 0",
          height:"48vh",
          display:"flex", flexDirection:"column",
          animation:"slideUp 0.3s ease-out",
          zIndex:1,
        }}
      >
        {/* Handle */}
        <div style={{ display:"flex", justifyContent:"center", padding:"12px 0 6px" }}>
          <div style={{ width:40, height:4, borderRadius:2, background:"rgba(255,255,255,0.2)" }} />
        </div>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 16px 12px", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ color:"#fff", fontSize:16, fontWeight:700, fontFamily:"DM Sans,sans-serif" }}>
            {loading ? "Comments" : `${comments.length} Comment${comments.length !== 1 ? "s" : ""}`}
          </span>
          <button
            onClick={onClose}
            style={{ background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:22, cursor:"pointer", lineHeight:1 }}>
            ✕
          </button>
        </div>

        {/* Comment list */}
        <div style={{ flex:1, overflowY:"auto", padding:"12px 16px" }}>
          {loading ? (
            <div style={{ display:"flex", justifyContent:"center", paddingTop:40 }}>
              <div style={{ width:28, height:28, borderRadius:"50%", border:"3px solid #333", borderTopColor:"#8B5FBF", animation:"spin 0.8s linear infinite" }} />
            </div>
          ) : comments.length === 0 ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"60%", gap:8 }}>
              <span style={{ fontSize:40 }}>💬</span>
              <p style={{ color:"rgba(255,255,255,0.4)", fontSize:14, fontFamily:"DM Sans,sans-serif", textAlign:"center" }}>
                No comments yet. Be first!
              </p>
            </div>
          ) : comments.map(c => (
            <div key={c.id} style={{ display:"flex", gap:10, marginBottom:16 }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:"linear-gradient(135deg,#667eea,#764ba2)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:14, flexShrink:0 }}>
                {c.username ? c.username[0].toUpperCase() : "?"}
              </div>
              <div style={{ flex:1 }}>
                <span style={{ color:"#fff", fontSize:13, fontWeight:600, fontFamily:"DM Sans,sans-serif" }}>
                  @{c.username}
                </span>
                <p style={{ color:"rgba(255,255,255,0.85)", fontSize:14, margin:"3px 0 2px", fontFamily:"DM Sans,sans-serif", lineHeight:1.4 }}>
                  {c.text}
                </p>
                <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:2 }}>
                  <span style={{ color:"rgba(255,255,255,0.3)", fontSize:11, fontFamily:"DM Sans,sans-serif" }}>
                    {new Date(c.created_at).toLocaleDateString()}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); const reason = window.prompt("Report this comment?\n\n1. Spam\n2. Harassment\n3. Hate speech\n4. Misinformation\n\nEnter reason:"); if(reason) alert("Comment reported. Our team will review it shortly."); }}
                    style={{ background:"none", border:"none", color:"rgba(255,255,255,0.25)", fontSize:11, cursor:"pointer", fontFamily:"DM Sans,sans-serif", padding:0 }}>
                    🚩 Report
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding:"6px 16px", background:"rgba(254,44,85,0.15)", borderTop:"1px solid rgba(254,44,85,0.2)" }}>
            <span style={{ color:"#fe2c55", fontSize:13, fontFamily:"DM Sans,sans-serif" }}>{error}</span>
          </div>
        )}

        {/* Input */}
        <div style={{
          padding:"10px 16px",
          paddingBottom:"max(16px, env(safe-area-inset-bottom))",
          borderTop:"1px solid rgba(255,255,255,0.08)",
          display:"flex", gap:10, alignItems:"center",
          background:"#1a1a1a",
        }}>
          <input
            ref={inputRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") submit(e); }}
            placeholder={t("addComment")}
            style={{
              flex:1, background:"rgba(255,255,255,0.08)", border:"none",
              borderRadius:20, padding:"10px 16px", color:"#fff",
              fontSize:14, fontFamily:"DM Sans,sans-serif", outline:"none",
            }}
          />
          <button
            onClick={submit}
            disabled={posting || !text.trim()}
            style={{
              background: (posting || !text.trim()) ? "rgba(139,95,191,0.4)" : "#8B5FBF",
              border:"none", borderRadius:20, padding:"10px 18px",
              color:"#fff", fontWeight:700, fontSize:14,
              cursor: (posting || !text.trim()) ? "default" : "pointer",
              fontFamily:"DM Sans,sans-serif", transition:"background 0.2s",
            }}>
            {posting ? "…" : t("post")}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

export default CommentDrawer;
